import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';

import { useAppConfig } from '@/hooks/use-app-config';
import { ldIdentifyCountAtom } from '@/state/launch-darkly.state';
import { isAgentProxyMode } from '@/utils/agent-proxy';
import { readBranchCookie } from '@/utils/branch-cookie';
import { isAnonymousContext } from '@/utils/launch-darkly-helpers';

import { labsAssignmentAtom } from './labs.state';
import {
  decideAssignment,
  hasAutoRedirected,
  performAssignmentAction,
} from './prototype-assignment';
import { PROTOTYPE_FLAG_DEFAULT, PrototypeFlag, readPrototypeFlag } from './prototype-flag';

/**
 * Keeps the `gaia_branch` cookie in sync with the `gaia-prototype` LaunchDarkly flag, and publishes
 * the Labs state to labsAssignmentAtom. The Labs UI reads that atom and opts in or out through
 * prototype-assignment.ts. Mount exactly once (in AppLayout).
 *
 * Acts only on a trustworthy flag value: a non-anonymous context, and a value that came from
 * LaunchDarkly rather than a fallback. Skipped in agent-proxy mode and local dev.
 *
 * Acts at most once per page load, on the first trustworthy value. Flag changes streamed in later
 * would otherwise reload the user's tabs mid-task; they take effect on the next load instead.
 *
 * @see docs/designs/labs-prototype-assignment.md
 */
export const useFrontendBranchAssignment = () => {
  const ldClient = useLDClient();
  // Subscribing to flags re-renders this hook when LaunchDarkly delivers new values.
  const flags = useFlags();
  // identify() doesn't re-render via useFlags when no flag value changes, so subscribe to it too.
  const identifyCount = useAtomValue(ldIdentifyCountAtom);
  const { data: appConfig } = useAppConfig();
  const setAssignment = useSetAtom(labsAssignmentAtom);

  const enabled = !!appConfig && !isAgentProxyMode(appConfig) && !import.meta.env.DEV;
  const flagRead = useMemo(
    () => (enabled ? readPrototypeFlag(ldClient, Object.keys(flags).length > 0) : undefined),
    [enabled, ldClient, flags, identifyCount]
  );
  const trusted = !!flagRead?.trusted && !isAnonymousContext(ldClient);

  // The first trustworthy value, latched for the rest of the page load.
  const [latched, setLatched] = useState<PrototypeFlag | null>(null);
  if (trusted && latched === null) setLatched(flagRead!.prototype);
  const flagBranch = latched ? latched.branch : null;

  // Read the cookie when deciding, not at mount: another tab may have changed it since.
  const { cookie, decision } = useMemo(() => {
    const cookie = readBranchCookie();
    return {
      cookie,
      decision: decideAssignment(cookie, flagBranch, !!flagBranch && hasAutoRedirected(flagBranch)),
    };
  }, [flagBranch]);

  // Without a trustworthy value, describe the prototype from the cookie so "Leave prototype" works.
  const prototype = useMemo(
    () => latched ?? (cookie?.status === 'labs' ? { branch: cookie.name } : PROTOTYPE_FLAG_DEFAULT),
    [latched, cookie]
  );

  useEffect(() => {
    performAssignmentAction(decision.action);
  }, [decision]);

  const { status } = decision;
  useEffect(() => {
    setAssignment({ status, prototype });
  }, [setAssignment, status, prototype]);
};
